import { apiFetch } from '../../auth/AuthContext';
/**
 * Query ClickHouse via the /ch proxy.
 * Credentials are injected server-side by Nginx — never sent from the client.
 */
async function chQuery<T = Record<string, unknown>>(sql: string): Promise<T[]> {
  const r = await apiFetch('/ch', {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain' },
    body: sql.trim() + '\nFORMAT JSON',
  });
  if (!r.ok) throw new Error(`ClickHouse error: ${r.status}`);
  const j = await r.json();
  return (j.data ?? []) as T[];
}

/** Fetch all coins already onboarded in binance_raw */
export async function fetchExistingCoins(): Promise<
  { symbol: string; tables: number; earliest: string; latest: string }[]
> {
  const rows = await chQuery<{
    symbol: string;
    tables: string;
    earliest: string;
    latest: string;
  }>(`
    SELECT
      symbol,
      count(DISTINCT source_table) AS tables,
      min(earliest)                AS earliest,
      max(latest)                  AS latest
    FROM (
      SELECT symbol, 'spot_ohlcv_5m'    AS source_table, min(open_time) AS earliest, max(open_time) AS latest FROM binance_raw.spot_ohlcv_5m    GROUP BY symbol
      UNION ALL
      SELECT symbol, 'futures_ohlcv_5m'  AS source_table, min(open_time) AS earliest, max(open_time) AS latest FROM binance_raw.futures_ohlcv_5m  GROUP BY symbol
      UNION ALL
      SELECT symbol, 'spot_ohlcv_1m'    AS source_table, min(open_time) AS earliest, max(open_time) AS latest FROM binance_raw.spot_ohlcv_1m    GROUP BY symbol
      UNION ALL
      SELECT symbol, 'funding_rate'     AS source_table, min(funding_time) AS earliest, max(funding_time) AS latest FROM binance_raw.funding_rate     GROUP BY symbol
    )
    GROUP BY symbol
    ORDER BY symbol
  `);
  return rows.map((r) => ({
    symbol: r.symbol.replace(/USDT$/, ''),
    tables: Number(r.tables),
    earliest: r.earliest,
    latest: r.latest,
  }));
}

/**
 * Validate coins exist on Binance via the onboard server (avoids CORS).
 * Returns a map of { COIN: true/false }.
 */
export async function validateCoinsOnBinance(
  coins: string[],
): Promise<Record<string, boolean>> {
  try {
    const params = new URLSearchParams({ coins: coins.join(',') });
    const r = await apiFetch(`/onboard/validate?${params}`);
    if (!r.ok) throw new Error(`Validation request failed: ${r.status}`);
    const j = await r.json();
    return j.results as Record<string, boolean>;
  } catch {
    // If server is unreachable, treat all as valid and let pull_coins.py handle it
    return Object.fromEntries(coins.map((c) => [c, true]));
  }
}

/**
 * Start coin onboarding via the onboard server (SSE stream).
 * Returns an EventSource that emits log lines.
 */
export function startOnboarding(
  coins: string[],
  from5m?: string,
  onLog?: (line: string) => void,
  onDone?: (success: boolean) => void,
): AbortController {
  const controller = new AbortController();

  const params = new URLSearchParams();
  params.set('coins', coins.join(','));
  if (from5m) params.set('from5m', from5m);

  const eventSource = new EventSource(`/onboard/run?${params}`);

  let aborted = false;

  eventSource.onmessage = (e) => {
    if (aborted) return;
    if (e.data === '[DONE]') {
      eventSource.close();
      onDone?.(true);
    } else if (e.data === '[ERROR]') {
      eventSource.close();
      onDone?.(false);
    } else if (e.data === '[CANCELLED]') {
      eventSource.close();
      // Don't call onDone — handleCancel manages state
    } else {
      onLog?.(e.data);
    }
  };

  eventSource.onerror = () => {
    eventSource.close();
    // Don't call onDone if we aborted — handleCancel manages state
    if (!aborted) onDone?.(false);
  };

  controller.signal.addEventListener('abort', () => {
    aborted = true;
    eventSource.close();
  });
  return controller;
}

/**
 * Cancel onboarding: kills the subprocess AND cleans up all data from ClickHouse + TOKENS list.
 * This is the primary cancel mechanism — it kills the process server-side first.
 */
export async function cancelOnboarding(coins: string[]): Promise<{
  status: string;
  killed?: boolean;
  cleaned?: string[];
  message?: string;
}> {
  const params = new URLSearchParams({ coins: coins.join(',') });
  const r = await fetch(`/onboard/cancel?${params}`, { method: 'POST' });
  if (!r.ok) throw new Error(`Cancel request failed: ${r.status}`);
  return r.json();
}

/**
 * Clean up coin data without killing a process (standalone cleanup).
 */
export async function cleanupCoins(
  coins: string[],
): Promise<{ status: string; cleaned?: string[]; message?: string }> {
  const params = new URLSearchParams({ coins: coins.join(',') });
  const r = await fetch(`/onboard/cleanup?${params}`, { method: 'POST' });
  if (!r.ok) throw new Error(`Cleanup request failed: ${r.status}`);
  return r.json();
}
